"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Layers, Search, Code as Code2, ArrowRight, Sparkles, ShieldCheck, Cpu, FileText, MousePointerClick } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
const executionPhases = [{
  id: "keyword-research",
  label: "1. Keyword Research",
  iconName: "Search",
  title: "Keyword Research & Content Gap Analysis",
  lead: "I conducted comprehensive buyer persona keyword mapping to identify untapped commercial search demand and fill high-intent content gaps.",
  points: ["Completed in-depth buyer persona keyword mapping across apparel lines", "Identified critical content gaps between current rankings and high-intent queries", "Mapped transactional search intent to specific collection categories and landing pages"],
  deliverable: "Buyer Persona Keyword Map & Content Gap Roadmap",
  progressValue: 100
}, {
  id: "on-page",
  label: "2. On-Page Optimization",
  iconName: "FileText",
  title: "On-Page Optimization",
  lead: "I overhauled on-page relevance across category headers, metadata, and product listings to ensure search engines and shoppers find exactly what they seek.",
  points: ["Rewrote meta titles and meta descriptions for key apparel categories", "Restructured H1, H2, and H3 header tags to reflect mapped keyword priorities", "Enhanced category descriptions and visual asset alt attributes for clarity"],
  deliverable: "Complete On-Page Optimization Across Collections",
  progressValue: 100
}, {
  id: "technical-seo",
  label: "3. Technical SEO Overhaul",
  iconName: "Code2",
  title: "Technical SEO Overhaul",
  lead: "I systematically cleared crawling bottlenecks, enhanced mobile performance, and added structured data for rich search results.",
  points: ["Resolved crawl errors and improved overall site architecture", "Enhanced mobile performance and fixed Core Web Vitals issues", "Added structured data markup for reviews and pricing validation"],
  deliverable: "Optimized Crawl Architecture & Validated Schema Markup",
  progressValue: 100
}, {
  id: "conversion",
  label: "4. Conversion Optimization",
  iconName: "MousePointerClick",
  title: "Conversion Optimization",
  lead: "I connected search intent with high-converting user journeys, ensuring organic traffic flowed naturally into completed purchases.",
  points: ["Improved internal linking across relevant categories and related items", "Refined calls-to-action across high-intent product landing pages", "Simplified checkout funnel pathways to reduce drop-offs"],
  deliverable: "Enhanced Internal Linking & Simplified Checkout Flow",
  progressValue: 100
}];
const highlights = [{
  title: "Direct Implementation",
  description: "No junior staff or hand-offs. I personally managed every script review, rewrite, and deployment guide.",
  iconName: "ShieldCheck"
}, {
  title: "Agile Sprints",
  description: "Coordinated with in-house developers via bi-weekly sprints to test, validate, and roll out technical updates.",
  iconName: "Layers"
}, {
  title: "Continuous Verification",
  description: "Daily automated monitoring of crawl errors, server logs, and indexed URL variations throughout the migration.",
  iconName: "Cpu"
}];
export default function Execution() {
  const [activeTab, setActiveTab] = useState("keyword-research");
  const shouldReduceMotion = useReducedMotion();
  const currentPhase = executionPhases.find(p => p.id === activeTab) || executionPhases[0];
  return <section id="fashionretailerexecution" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              Case Study Execution
            </Badge>
            <span className="text-xs font-medium text-muted-foreground">
              UK Fashion &amp; Apparel Retailer
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            How I Executed the Strategy Step by Step
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            As an independent SEO consultant, you work directly with me. Here is the exact, hands-on
            implementation roadmap I applied to transform this retailer&apos;s organic search presence and eliminate crawl bottlenecks.
          </p>
        </div>

        <div className="mt-12 lg:mt-16">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-8">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 h-auto p-1.5 gap-2 bg-muted/80 rounded-xl">
              {executionPhases.map((phase, i) => <TabsTrigger key={phase.id} data-index={i} value={phase.id} className="py-3 px-4 text-sm font-semibold rounded-lg text-foreground data-[state=active]:bg-background data-[state=active]:text-primary transition-[color,box-shadow]">
                  <span className="truncate">{phase.label}</span>
                </TabsTrigger>)}
            </TabsList>

            <TabsContent value={activeTab} className="mt-0 outline-none">
              <motion.div key={activeTab} initial={{
              opacity: shouldReduceMotion ? 1 : 0,
              y: shouldReduceMotion ? 0 : 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.35,
              ease: "easeOut"
            }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-7 space-y-6">
                  <Card className="border border-border/80 shadow-md bg-card">
                    <CardHeader className="space-y-2">
                      <div className="flex items-center gap-2 text-primary">
                        <Sparkles className="size-5 text-accent" />
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          Strategic Execution Focus
                        </span>
                      </div>
                      <CardTitle className="text-2xl font-bold text-card-foreground font-default">
                        {currentPhase.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground font-default">
                        {currentPhase.lead}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6">
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                          Actions Delivered Personally
                        </h3>
                        <div className="grid grid-cols-1 gap-3">
                          {currentPhase.points.map((point, i) => <div key={i} data-index={i} className="flex items-start gap-3 rounded-lg border border-border/60 bg-background p-3.5">
                              <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm font-medium text-foreground">
                                {point}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      <div className="rounded-xl bg-secondary/50 p-4 border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-secondary-foreground uppercase tracking-wider">
                            Target Outcome
                          </span>
                          <span className="text-xs font-bold text-primary">
                            100% Completed
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-foreground mb-3">
                          {currentPhase.deliverable}
                        </p>
                        <Progress value={currentPhase.progressValue} className="h-2 bg-muted" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="lg:col-span-5 space-y-6">
                  <Card className="border border-border/80 shadow-md bg-card">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold text-card-foreground">
                        Why Direct Consultant Access Matters
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        No agency account managers. You communicate straight with the strategist executing your audit.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3.5 p-3 rounded-lg bg-background border border-border/50">
                          <div className="rounded-md bg-muted p-2 text-primary shrink-0">
                            {item.iconName === "ShieldCheck" && <ShieldCheck className="size-4" />}
                            {item.iconName === "Layers" && <Layers className="size-4" />}
                            {item.iconName === "Cpu" && <Cpu className="size-4" />}
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">
                              {item.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>)}
                    </CardContent>
                  </Card>

                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 shadow-xs flex flex-col justify-between gap-4">
                    <div>
                      <h4 className="text-base font-bold text-foreground">
                        Need this level of execution for your store?
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        I can audit your ecommerce infrastructure and design a tailored growth roadmap.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                        <Link to="/ContactPage">
                          Book a Free SEO Consultation
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm" className="border-border hover:bg-accent hover:text-accent-foreground">
                        <Link to="/CaseStudiesPage">
                          All Case Studies
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>;
}